import React, { useState, type ReactNode } from "react";
import ClassNames from "classnames";
import type { FieldError } from "react-hook-form";
import "./TemplateSelect.scss";
import TemplateBrowser from "./TemplateBrowser/TemplateBrowser.js";
import type { TemplateOption } from "./TemplateBrowser/TemplateBrowser";
import {
  Tooltip,
  SeemPopover,
  FormFieldError,
  getDataAnalyticsProps,
} from "@SeemplicityDev/shared";

// Mirrors TemplateBrowser's `TemplateId` (the consumer this prop is forwarded to).
type TemplateId = string | number;

export type TemplateSelectSize =
  | "xxs"
  | "xs"
  | "s"
  | "m"
  | "l"
  | "xl"
  | "xxl"
  | "max"
  | "custom";

// Template value/option shape. Reconciled with TemplateBrowser's `TemplateOption`
// (the consumer this select delegates to) so options/value pass through without
// structural mismatch. Extends `TemplateOption` and keeps the open index
// signature for the extra server-payload members the runtime carries.
export interface TemplateSelectOption extends TemplateOption {
  [key: string]: unknown;
}

export interface TemplateSelectProps {
  "data-testid": string;
  options?: TemplateSelectOption[];
  value?: TemplateSelectOption | null;
  onChange?: (template: TemplateSelectOption | undefined) => void;
  onCopyTemplateData?: (selectedTemplateData: TemplateSelectOption) => void;
  onApplyTemplateData?: (selectedTemplateData: TemplateSelectOption) => void;
  error?: FieldError;
  wrapperClassName?: string;
  className?: string;
  type?: string;
  name?: string;
  label?: string | null;
  labelTop?: boolean;
  size?: TemplateSelectSize;
  required?: boolean;
  hideLabelHelper?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  labelWidth?: string | false;
  isInline?: boolean;
  isLarge?: boolean;
  placeholder?: string;
  leftIcon?: string;
  rightIcon?: string;
  noMargin?: boolean;
  withHelp?: string;
  hideError?: boolean;
  description?: string;
  labelRight?: ReactNode;
  onSelectTemplateToDisplay?: (templateId: TemplateId) => void;
  isGenericAreaLoading?: boolean;
  children?: ReactNode;
  onEventTriggered?: (eventName: string) => void;
  "data-analytics-event"?: string;
  "data-analytics-props"?: string;
}

const TemplateSelect: React.FC<TemplateSelectProps> = (props) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const {
    options,
    value,
    onChange = () => {},
    onCopyTemplateData = () => {},
    onApplyTemplateData = () => {},
    error,
    wrapperClassName,
    label,
    labelTop = false,
    size = "max",
    required = false,
    hideLabelHelper = false,
    disabled = false,
    readOnly = false,
    labelWidth = false,
    isInline,
    isLarge,
    placeholder = "",
    leftIcon = "",
    rightIcon = "",
    noMargin = false,
    "data-testid": dataTestId,
    withHelp = "",
    hideError,
    description = "",
    labelRight = "",
    onSelectTemplateToDisplay,
    isGenericAreaLoading = false,
    children,
    onEventTriggered = () => {},
  } = props;

  const classNameWrapper = ClassNames(
    "input-wrap",
    "input-wrap-text",
    wrapperClassName && wrapperClassName,
    {
      "has-label": label !== null,
      "has-label-top": labelTop,
      "hide-label-helper": hideLabelHelper,
      "is-disabled": disabled,
      "has-read-only": readOnly,
      "has-left-icon": leftIcon,
      "has-right-icon": rightIcon,
      "has-size-xxs": size === "xxs",
      "has-size-xs": size === "xs",
      "has-size-s": size === "s",
      "has-size-m": size === "m",
      "has-size-l": size === "l",
      "has-size-xl": size === "xl",
      "has-size-xxl": size === "xxl",
      "has-size-max": size === "max",
      "has-label-width": labelWidth,
      "has-size-custom": size === "custom",
      "is-inline": isInline,
      "is-large": isLarge,
      "has-no-margin": noMargin,
      "has-error": error,
      "with-help": withHelp?.length,
    },
    props.className
  );

  const handleOnCopyTemplateData = (
    selectedTemplateData?: TemplateSelectOption
  ) => {
    setIsPopoverOpen(false);
    if (!selectedTemplateData) {
      return;
    }
    onCopyTemplateData(selectedTemplateData);
    onChange(undefined);
    onEventTriggered("Ticket_Templates_Template_Copied");
  };

  const handleOnApplyTemplateData = (
    selectedTemplateData?: TemplateSelectOption
  ) => {
    setIsPopoverOpen(false);
    if (!selectedTemplateData) {
      return;
    }
    onChange(selectedTemplateData);
    onEventTriggered("Ticket_Templates_Template_Applied");
    onApplyTemplateData(selectedTemplateData);
  };

  const getDropdownInputLine = () => {
    return (
      <div
        className={`input-holder`}
        onClick={() => {
          if (!isPopoverOpen) {
            onEventTriggered("Ticket_Templates_Open_Templates_Dropdown");
          }

          setIsPopoverOpen((pervState) => !pervState);
        }}
        {...getDataAnalyticsProps(props)}
      >
        {leftIcon && (
          <div className={"input-icon-wrap"}>
            <i className={`input-left-icon seem-icon ${leftIcon}`} />
          </div>
        )}
        <div className={`single-value-wrapper ${!value ? "placeholder" : ""}`}>
          {!!value ? value.name : placeholder}
        </div>
        <i
          className={`dropdown-icon seem-icon ${
            isPopoverOpen ? "seem-icon-chevron-up" : "seem-icon-chevron-down"
          }`}
        ></i>
        {labelRight && (
          <label className={`input-label-right`}>{labelRight}</label>
        )}
        {rightIcon && (
          <div className={"input-icon-wrap"}>
            <i className={`input-right-icon seem-icon ${rightIcon}`} />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={classNameWrapper}>
        {label && (
          <label
            className={`input-label`}
            style={{ width: labelWidth ? labelWidth : "" }}
            data-testid={`${dataTestId}-label`}
          >
            {label}
            {!required && !hideLabelHelper && (
              <span className={"label-helper"}>Optional</span>
            )}
            {withHelp && (
              <Tooltip type="info" content={withHelp}>
                <i className={`seem-icon seem-icon-help`} />
              </Tooltip>
            )}
          </label>
        )}

        {description && (
          <div className={`input-description`}>{description}</div>
        )}
        <SeemPopover
          isOpen={isPopoverOpen}
          useSameWidth={true}
          onOuterAction={() => {
            setIsPopoverOpen(false);
          }}
          body={
            <TemplateBrowser
              options={options}
              defaultTemplate={value ?? undefined}
              onCopyTemplateData={handleOnCopyTemplateData}
              onSelectTemplate={handleOnApplyTemplateData}
              onSelectTemplateToDisplay={onSelectTemplateToDisplay}
              isGenericAreaLoading={isGenericAreaLoading}
            >
              {children}
            </TemplateBrowser>
          }
        >
          {getDropdownInputLine()}
        </SeemPopover>

        {error && !hideError && (
          <FormFieldError
            errorMessage={error.message}
            errorType={error.type}
            fieldName={dataTestId}
          />
        )}
      </div>
    </>
  );
};

export default TemplateSelect;
